import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import mystyles from './styles/table.module.css';
import chrome from './consultationTableChrome.module.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import axiosInstance from "../../AxiosInstance";

function tooltipTitleFromString(value) {
    if (value == null || typeof value !== 'string') return undefined;
    const t = value.trim();
    return t || undefined;
}

function primitiveTooltipString(value, asBooleanColumn) {
    if (asBooleanColumn) return value ? 'Sí' : 'No';
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'object') return undefined;
    return String(value);
}

function resolveTooltipTitle(row, col, getValueFromString, isBooleanColumn = false) {
    if (!row || !col || col.buttons || col.image) return undefined;
    if (typeof col.tooltipText === 'function') {
        try {
            const raw = col.tooltipText(row);
            return tooltipTitleFromString(raw != null ? String(raw) : '');
        } catch {
            return undefined;
        }
    }
    if (col.tooltipAccessor) {
        const v = getValueFromString(row, col.tooltipAccessor);
        return tooltipTitleFromString(primitiveTooltipString(v, typeof v === 'boolean'));
    }
    const v = getValueFromString(row, col.internal);
    const asBool =
        col.isBoolean === true || isBooleanColumn === true || typeof v === 'boolean';
    return tooltipTitleFromString(primitiveTooltipString(v, asBool));
}

/** Esquina superior izquierda del tooltip exactamente en el puntero (coords. de viewport). */
function consultTipDesiredFromPointer(e) {
    return { x: e.clientX, y: e.clientY };
}

const CONSULT_TIP_VP_MARGIN = 8;

/** Mantiene el tooltip dentro del viewport usando su tamaño real (no un “reserveH” fijo que aleja el cuadro del ratón). */
function clampConsultTipToViewport(desiredX, desiredY, tipEl) {
    if (typeof window === 'undefined') {
        return { x: desiredX, y: desiredY };
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let tw = Math.min(480, vw - 2 * CONSULT_TIP_VP_MARGIN);
    let th = 48;
    if (tipEl && tipEl.isConnected) {
        const r = tipEl.getBoundingClientRect();
        tw = Math.max(r.width, 28);
        th = Math.max(r.height, 24);
    }
    const x = Math.round(
        Math.max(CONSULT_TIP_VP_MARGIN, Math.min(desiredX, vw - tw - CONSULT_TIP_VP_MARGIN))
    );
    const y = Math.round(
        Math.max(CONSULT_TIP_VP_MARGIN, Math.min(desiredY, vh - th - CONSULT_TIP_VP_MARGIN))
    );
    return { x, y };
}

export default function TableComponent({
    fetchUrl,
    currentPage,
    pageSize,
    cols,
    rows,
    pull,
    withIndex,
    noPagination,
    defaultFilters,
    rowHover,
    consultationChrome,
    suppressFetch,
}) {
    const [activeFilter, setActiveFilter] = useState(null);
    const filterInputRef = useRef(null);
    const [filters, setFilters] = useState({...(defaultFilters || {})});
    const [consultFloatTipText, setConsultFloatTipText] = useState(null);
    const consultFloatTipRef = useRef(null);
    const consultTipDesiredRef = useRef({ x: 0, y: 0 });

    const setField = (name, value) => {
        setFilters({
            ...filters,
            [name]: value
        });
    }

    const getValueFromString = (object, accessString) => {
        let accessParts = accessString.split(".");
        let value = object;

        for (let i = 0; i < accessParts.length; i++) {
            if (!value) {
                return "";
            }
            let accessPart = accessParts[i];
            value = value[accessPart];
        }

        return value;
    }

    const renderTh = (col, index) => {
        const filterVal = col.filterInternal ? filters[col.filterInternal] : filters[col.internal];
        const showFilterStar =
            filterVal !== undefined && filterVal !== null && String(filterVal).trim() !== '';

        if(index === activeFilter && col.filter === true) {
            return (
                <th key={index}>
                    <div ref={filterInputRef}>
                        <input value={col.filterInternal ? filters[col.filterInternal] : filters[col.internal]} onChange={(event) => setField(col.filterInternal ? col.filterInternal : col.internal, event.target.value)} type={'text'} placeholder={`Filter ${col.title}`}/>
                    </div>
                </th>
            )
        } else {
            const isLastHeader = index === cols.length - 1;
            const consultHead =
                consultationChrome && withIndex && !isLastHeader ? chrome.consultHeadClamp : undefined;
            const headTitleAttr = consultHead ? col.title : undefined;
            return (
                <th key={index} className={consultHead || undefined} title={headTitleAttr} onClick={() => setActiveFilter(index)}>
                    {col.title}
                    {showFilterStar ? '' : ''}
                </th>
            );
        }
    }

    useEffect(() => {
        if (suppressFetch) {
            return;
        }
        if (!fetchUrl || typeof pull !== 'function') {
            return;
        }
        const queryParams = Object.entries(filters)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
        const url = `${fetchUrl}/?${queryParams}`;
        axiosInstance.get(url).then((response) => {
            if (noPagination) {
                pull(response.data);
            } else {
                pull(response.data.results);
            }
        });
    }, [filters, suppressFetch, fetchUrl, pull, noPagination]);

    const repositionConsultFloatTip = () => {
        const el = consultFloatTipRef.current;
        if (!el || !el.isConnected || typeof window === 'undefined') return;
        const d = consultTipDesiredRef.current;
        const c = clampConsultTipToViewport(d.x, d.y, el);
        el.style.left = `${c.x}px`;
        el.style.top = `${c.y}px`;
        el.style.transform = 'none';
        el.style.margin = '0';
    };

    const applyConsultFloatTipPosition = (e) => {
        consultTipDesiredRef.current = consultTipDesiredFromPointer(e);
        repositionConsultFloatTip();
    };

    const clearConsultFloatTip = () => setConsultFloatTipText(null);
    const openConsultFloatTip = (e, text) => {
        if (!text) return;
        consultTipDesiredRef.current = consultTipDesiredFromPointer(e);
        setConsultFloatTipText(text);
    };

    useLayoutEffect(() => {
        if (!consultFloatTipText || typeof window === 'undefined') return;
        repositionConsultFloatTip();
        const id = window.requestAnimationFrame(() => repositionConsultFloatTip());
        return () => window.cancelAnimationFrame(id);
    }, [consultFloatTipText]);

    const tableClassName = [
        mystyles.table,
        rowHover ? mystyles.tableWithRowHover : '',
        consultationChrome ? chrome.tablePremium : '',
        consultationChrome && withIndex ? chrome.consultationWithIndex : '',
    ]
        .filter(Boolean)
        .join(' ');

    const tableInner = (
        <table className={tableClassName}>
            <thead>
            <tr>
            {withIndex && (
                <th
                    key="thead-index"
                    className={
                        consultationChrome && withIndex ? chrome.consultHeadIndex : undefined
                    }
                    title={consultationChrome && withIndex ? 'N°' : undefined}
                >
                    N°
                </th>
            )}
            {cols.map((col, index) => renderTh(col, index))}
            </tr>
            </thead>
            <tbody>
            {rows.map((row, index) => (
                <tr key={index}>
                    {withIndex && (
                        <td
                            key="td-index"
                            className={
                                consultationChrome && withIndex
                                    ? `${chrome.consultTdClamp} ${chrome.consultTdIndex}`
                                    : undefined
                            }
                            title={
                                consultationChrome && withIndex
                                    ? String(
                                          (index + 1) +
                                              ((currentPage - 1 ?? 1) * (pageSize ?? 0))
                                      )
                                    : undefined
                            }
                        >
                            {(index + 1) + ((currentPage - 1 ?? 1) * (pageSize ?? 0))}
                        </td>
                    )}
                    {cols.map((col, colIndex) => {
                        const isActionsCol = colIndex === cols.length - 1;
                        const truncClass =
                            consultationChrome && withIndex && !isActionsCol
                                ? chrome.consultTdClamp
                                : '';
                        const titleAttr =
                            consultationChrome && withIndex && !isActionsCol
                                ? resolveTooltipTitle(
                                      row,
                                      col,
                                      getValueFromString,
                                      col.isBoolean === true
                                  )
                                : undefined;
                        const cellInner = col.buttons ? (
                            <span className={mystyles.actionButtons}>
                                {col.buttons.map((button, buttonIndex) => (
                                    <div
                                        key={buttonIndex}
                                        role={'button'}
                                        onClick={() => {
                                            button.onClick(row.id);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={button.icon} color={button.color} />
                                    </div>
                                ))}
                            </span>
                        ) : (
                            <>
                                {col.image ? (
                                    <img
                                        src={getValueFromString(row, col.image.url)}
                                        alt={col.title}
                                        width={'50px'}
                                        height={'50px'}
                                        style={{
                                            borderRadius: '5px',
                                            objectFit: 'contain',
                                            backgroundColor: '#F7F8F9',
                                        }}
                                    />
                                ) : (
                                    <>
                                        {col.isBoolean ? (
                                            <span>
                                                {getValueFromString(row, col.internal) ? 'Sí' : 'No'}
                                            </span>
                                        ) : (
                                            <>
                                                {col.code ? (
                                                    <>{col.code(row, getValueFromString(row, col.internal))}</>
                                                ) : (
                                                    <span>{getValueFromString(row, col.internal)}</span>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        );
                        return (
                            <td key={colIndex} className={truncClass || undefined}>
                                {truncClass && !col.buttons && !col.image ? (
                                    <div
                                        className={chrome.consultTdHoverTarget}
                                        title={titleAttr}
                                        onMouseEnter={(ev) =>
                                            titleAttr ? openConsultFloatTip(ev, titleAttr) : undefined
                                        }
                                        onMouseMove={(ev) => {
                                            if (!titleAttr) return;
                                            applyConsultFloatTipPosition(ev);
                                        }}
                                        onMouseLeave={clearConsultFloatTip}
                                    >
                                        {cellInner}
                                    </div>
                                ) : (
                                    cellInner
                                )}
                            </td>
                        );
                    })}
                </tr>
            ))}
            </tbody>
        </table>
    );

    const consultTooltipPortal =
        consultFloatTipText && typeof document !== 'undefined'
            ? createPortal(
                  <div
                      ref={consultFloatTipRef}
                      role="tooltip"
                      className={chrome.consultFloatTooltip}
                      style={{
                          position: 'fixed',
                          left: 0,
                          top: 0,
                          margin: 0,
                          transform: 'none',
                      }}
                  >
                      {consultFloatTipText}
                  </div>,
                  document.body
              )
            : null;

    return (
        <div
            className={consultationChrome ? chrome.tableShell : undefined}
            style={{ overflowX: 'auto' }}
        >
            {tableInner}
            {consultTooltipPortal}
        </div>
    );
}